<script lang="ts">
  import moment from "moment";
  import formatPhone from "@shared/filters/phone.js";
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import StateSettingsStore from "@shared/svelte/Stores/StateSettingsStore";
  import type { AccountApplication } from "@shared/types/AccountApplication";
  import type { HTTPParams } from "@shared/types/HTTP";
  import Counter from "./Counter.svelte";
  import { email as emailValidator, pattern, required } from "svelte-forms2/validators";
  import EmailInput from "@shared/svelte/Components/EmailInput.svelte";
  import Flash from "@shared/services/flashv2.js";
  import Footer from "./Footer.svelte";
  import { form as svelteForm, field } from "svelte-forms2";
  import FormInputError from "@shared/svelte/Components/FormInputError.svelte";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import TextInput from "@shared/svelte/Components/TextInput.svelte";
  import { onMount } from "svelte";

  export let id: number = null;
  export let application: AccountApplication = <AccountApplication>{};
  export let exitForm: () => void;
  export let goToNextStep: (id: number, nextStep: number) => void;
  export let step: number = 2;
  export let formDisabled: boolean = false;

  const ownerSignature = field("owner_signature", application.owner_signature || "", [required()]);
  const ownerVerificationEmail = field(
    "owner_verification_email",
    application.owner_verification_email || "",
    [required(), emailValidator()]
  );

  let legalese;

  onMount(async () => {
    const req: HTTPParams = {
      method: "GET",
      url: `/api/retailer/account_applications/${id}/legalese`,
    };

    try {
      const response = await HttpPromise(req);
      legalese = response.legalese;
    } catch {}
  });

  const signApplicationForm = svelteForm(ownerSignature, ownerVerificationEmail);

  const saveForm = async () => {
    if (!$signApplicationForm.valid) return;
    if (!$signApplicationForm.dirty) return;

    let req: HTTPParams = {
      method: "PUT",
      url: `/api/retailer/account_applications/${id}`,
      data: {
        account_application: {
          owner_signature: $ownerSignature.value,
          owner_verification_email: $ownerVerificationEmail.value,
        },
      },
    };

    try {
      await HttpPromise(req);
    } catch (e) {
      return e.errors.forEach((message: string) =>
        Flash.now({ type: "error", message, rawHtml: true })
      );
    }
  };

  const saveAndGoToNextStep = async () => {
    await signApplicationForm.validate();
    if (!$signApplicationForm.valid) {
      return;
    } else {
      const req: HTTPParams = {
        method: "PUT",
        url: `/api/retailer/account_applications/${id}`,
        data: {
          account_application: {
            owner_signature: $ownerSignature.value,
            owner_verification_email: $ownerVerificationEmail.value,
            status: "verification_required",
          },
        },
      };

      try {
        await HttpPromise(req);

        application.owner_signature = $ownerSignature.value;
        application.owner_verification_email = $ownerVerificationEmail.value;
      } catch (e) {
        return e.errors.forEach((message: string) =>
          Flash.now({ type: "error", message, rawHtml: true })
        );
      }
      goToNextStep(id, 3);
    }
  };

  const saveAndExit = async () => {
    await saveForm();
    exitForm();
  };

  function focusOwnerSignatureInput() {
    document.getElementsByName("owner_signature")[0].focus();
  }

</script>

<style>
  @import url("https://fonts.googleapis.com/css?family=Herr+Von+Muellerhoff");

  .signature-field {
    font-family: "Herr Von Muellerhoff";
    font-size: 3.375rem;
    line-height: 4.5rem;
    background-color: #eaeaea;
    min-height: 6rem;
    padding-top: 22px;
    user-select: none;
  }

</style>

<main class="fizz-surface">
  <section class="fizz-container">
    <Counter {step} />
    <div
      class="fizz-container fizz-stack-16"
      class:fizz-inset-squish-96={!$LayoutStore.isMobile}
      class:fizz-inset-square-16={$LayoutStore.isMobile}
    >
      <div
        class="fizz-inset-vertical-24 fizz-border-bottom fizz-align-content-start"
        class:fizz-flex-row-32-responsive={!$LayoutStore.isMobile}
        class:fizz-flex-row-responsive={$LayoutStore.isMobile}
      >
        <div class="fizz-flex-item">
          <h3 class="fizz-heading-3 fizz-stack-8">Review information</h3>
          <p class="fizz-text-medium fizz-stack-16 fizz-text-subdued">
            Review the information you entered to make sure everything is correct, then sign and
            submit this application.
          </p>
        </div>
        <div class="fizz-flex-item" class:fizz-full-width={$LayoutStore.isMobile}>
          <div class="fizz-stack-32">
            <div class="fizz-stack-24">
              <p class="fizz-text-strong fizz-text-large fizz-stack-16">Account details</p>
              <p class="fizz-text-strong fizz-stack-8">Distributor</p>
              <p>
                {application.distributor.name}
              </p>
            </div>
            <div class="fizz-flex-row-responsive fizz-stack-24 fizz-align-content-start">
              <div class="fizz-flex-item fizz-flow-8">
                <p class="fizz-text-strong">Buyer details</p>
                <p>{application.buyer_contact_name}</p>
                <p>{application.email}</p>
                <p>{formatPhone(application.phone)}</p>
              </div>
              <div class="fizz-flex-item fizz-flow-8">
                <p class="fizz-text-strong">Owner details</p>
                <p>{application.owner_name}</p>
                <p>{application.owner_email}</p>
                <p>{formatPhone(application.owner_phone)}</p>
              </div>
            </div>
            <div class="fizz-flex-row-responsive fizz-stack-24 fizz-align-content-start">
              <div class="fizz-flex-item fizz-flow-8">
                <p class="fizz-text-strong">Billing address</p>
                <p>{application.billing_contact}</p>
                <p>{application.billing_contact_email}</p>
                <p>{formatPhone(application.billing_contact_phone)}</p>
                <p>{application.billing_address}</p>
              </div>
              <div class="fizz-flex-item fizz-flow-8">
                <p class="fizz-text-strong">Delivery address & time</p>
                <p>{application.delivery_address}</p>
                <p>{application.delivery_instructions}</p>
              </div>
            </div>
          </div>
          <div class="fizz-stack-32">
            <div class="fizz-stack-24">
              <p class="fizz-text-strong fizz-text-large fizz-stack-24">Business details</p>
              <p class="fizz-text-strong fizz-stack-8">Full Corporate Name</p>
              <p>
                {application.full_corporate_name}
              </p>
            </div>
            <div class="fizz-flex-row-responsive fizz-stack-24 fizz-align-content-start">
              <div class="fizz-flex-item fizz-flow-8">
                <p class="fizz-text-strong">Employer identification number (EIN)</p>
                <p>{application.employer_identification_number}</p>
              </div>
              <div class="fizz-flex-item fizz-flow-8">
                <p class="fizz-text-strong">Resale certification number</p>
                <p>{application.resale_cert_number}</p>
              </div>
            </div>
            <div class="fizz-flex-row-responsive fizz-stack-24 fizz-align-content-start">
              <div class="fizz-flex-item fizz-flow-8">
                <p class="fizz-text-strong">License number</p>
                <p>{application.liquor_license}</p>
              </div>
              <div class="fizz-flex-item fizz-flow-8">
                <p class="fizz-text-strong">License expiration</p>
                <p>
                  {#if !application.license_expiration_date}
                    Pending
                  {:else}
                    {moment(application.license_expiration_date).format("MMMM DD, YYYY")}
                  {/if}
                </p>
              </div>
            </div>
            {#if !!application.liquor_license_image_name}
              <div class="fizz-flex-row-responsive fizz-stack-24 fizz-align-content-start">
                <div class="fizz-flex-item fizz-flow-8">
                  <p class="fizz-text-strong">Liquor license image</p>
                  <p>{application.liquor_license_image_name}</p>
                </div>
              </div>
            {/if}
          </div>
          {#if !$StateSettingsStore.cod_enabled || application.sales_rep_name}
            <div class="fizz-stack-32">
              <div class="fizz-stack-24">
                {#if !$StateSettingsStore.cod_enabled}
                  <p class="fizz-text-strong fizz-text-large fizz-stack-24">References</p>
                  <div class="fizz-stack-24">
                    <p class="fizz-text-strong fizz-stack-8">Trade references</p>
                    <p>
                      {application.trade_references}
                    </p>
                  </div>
                  <div class="fizz-stack-24">
                    <p class="fizz-text-strong fizz-stack-8">Bank references</p>
                    <p>
                      {application.bank_references}
                    </p>
                  </div>
                {/if}
                {#if application.sales_rep_name}
                  <div class="fizz-stack-24">
                    <p class="fizz-text-strong fizz-stack-8">Sales rep</p>
                    <p>
                      {application.sales_rep_name}
                    </p>
                  </div>
                {/if}
              </div>
            </div>
          {/if}
        </div>
      </div>
      <form
        class="fizz-container fizz-inset-vertical-24 fizz-stack-16"
        aria-label="sign-application-form"
      >
        <fieldset
          class="fizz-flex-row-responsive fizz-align-content-start"
          disabled={formDisabled || null}
        >
          <div class="fizz-flex-item">
            <h3 class="fizz-heading-3 fizz-stack-8">Sign application</h3>
            <p class="fizz-text-medium fizz-stack-16 fizz-text-subdued">
              A signature is required to complete this application.
            </p>
          </div>
          <div class="fizz-flex-item">
            <legend
              class="fizz-stack-12 fizz-flex-row-responsive fizz-space-between fizz-full-width"
            >
              <h4 class="fizz-heading-4">Owner's signature</h4>
            </legend>
            <TextInput
              name="owner_signature"
              label="First and last name"
              field={ownerSignature}
              onblur={saveForm}
            >
              <p class="fizz-text-subdued">Enter name to create signature</p>
              <FormInputError form={signApplicationForm} errorName="owner_signature.required">
                First and last name are required
              </FormInputError>
            </TextInput>
            <EmailInput
              name="owner_verification_email"
              label="Email address"
              field={ownerVerificationEmail}
              onblur={saveForm}
            >
              <p class="fizz-text-subdued">A verification email will be sent to that email</p>
              <FormInputError
                form={signApplicationForm}
                errorName="owner_verification_email.required"
              >
                Email address is required
              </FormInputError>
              {#if !$signApplicationForm.hasError("owner_verification_email.required")}
                <FormInputError
                  form={signApplicationForm}
                  errorName="owner_verification_email.not_an_email"
                >
                  Email address is invalid
                </FormInputError>
              {/if}
            </EmailInput>
            <div class="fizz-stack-16">
              <p class="fizz-text-small fizz-text-strong fizz-text-subdued">SIGNATURE PREVIEW:</p>
              <p class="signature-field fizz-inset-square-16" on:click={focusOwnerSignatureInput}>
                {$ownerSignature.value}
              </p>
            </div>
            <div class="fizz-text-small">
              <p>{legalese}</p>
            </div>
          </div>
        </fieldset>
      </form>
    </div>
  </section>
</main>
<Footer
  backEnabled={true}
  {id}
  {saveAndExit}
  submit={saveAndGoToNextStep}
  isDisabled={false || formDisabled}
  {formDisabled}
  dataTestId={"verify-and-submit-button"}
  submitButtonText={"Verify and submit"}
/>
